import { DatePickerField, InputField } from "@/components";
import { useDebounce } from "@/hooks";
import {
  useCampaignCopy,
  useCampaignDownloadReport,
  useCampaignList,
  useCampaignSwitchStatus,
} from "@/services/hooks/campaign/useCampaign";
import ExcelJS from "exceljs";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Card, IconAdd, IconSearch, Table } from "tvs-unified-lib";
import { campaignColumns } from "./helpers";
import { Header, Root, Title } from "./styles";
import { DataTypeList } from "./types";

interface FormValues {
  dateFilter: string;
  search: string;
}

export type Pagination = {
  pageIndex: number;
  pageSize: number;
  total: number;
};

export const Campaigns = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isInitialMount = useRef(true);
  const [selected, setSelected] = useState<number[]>([]);

  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      dateFilter: "",
      search: "",
    },
  });
  const [params, setParams] = useState<{
    page: number;
    limit: number;
    search?: string;
    date?: string;
  }>({
    page: 1,
    limit: 10,
  });

  const { data: campaignList, isLoading, refetch } = useCampaignList(params);
  const { mutation: switchStatus } = useCampaignSwitchStatus();
  const { mutation: copyCampaign } = useCampaignCopy();
  const { mutate: downloadReport, isPending: isReportLoading } =
    useCampaignDownloadReport();

  const searchValue = watch("search");
  const dateFilterValue = watch("dateFilter");

  const debouncedSetParams = useCallback(
    useDebounce((newParamsPartial: Omit<typeof params, "limit">) => {
      setParams((prev) => ({
        ...prev,
        ...newParamsPartial,
      }));
    }, 500),
    []
  );

  useEffect(() => {
    if (campaignList?.data) {
      campaignList?.data.forEach((item: DataTypeList) => {
        const isActive = item.status === "ACTIVE";
        if (isActive && !selected.includes(item.id)) {
          setSelected((t) => [...t, item.id]);
        }
      });
    }
  }, [JSON.stringify(campaignList?.data)]);

  useEffect(() => {
    const formattedDate = dateFilterValue
      ? moment(dateFilterValue).format("YYYY-MM-DD")
      : undefined;
    debouncedSetParams({
      page: 1,
      search: searchValue || undefined,
      date: formattedDate,
    });
    return () => debouncedSetParams.cancel();
  }, [searchValue, dateFilterValue, debouncedSetParams]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      refetch();
    }
  }, [params, refetch]);

  const handleCopyCampaign = (id: number) => {
    if (!id) return;
    copyCampaign.mutate(id, {
      onSuccess: () => {
        refetch();
        enqueueSnackbar("Campaign copied successfully", {
          variant: "success",
        });
      },
      onError: (e) => {
        enqueueSnackbar(e.message, {
          variant: "error",
        });
      },
    });
  };

  const toggleCampaignById = async (id: number, checked: boolean) => {
    if (!id) return;
    const data = {
      id: id,
      status: checked ? "ACTIVE" : "INACTIVE",
    };
    switchStatus.mutate(data, {
      onSuccess: () => {
        refetch();
      },
      onError: (e) => {
        enqueueSnackbar(e.message, {
          variant: "error",
        });
      },
    });
  };

  const handleDownloadReport = (id: number) => {
    if (isReportLoading) return;

    downloadReport(id, {
      onSuccess: async ({ data }) => {
        if (!data.length) {
          enqueueSnackbar("No data found", {
            variant: "warning",
          });
          return;
        }
        const headers = [
          "Account ID",
          "Account Name",
          "Campaign ID",
          "Campaign Channel",
          "Template ID",
          "Template Name",
          "Trigger Status",
          "Data Source",
          "Data File",
        ];

        try {
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet("Campaign Report");

          worksheet.addRow(headers);

          const headerRow = worksheet.getRow(1);
          headerRow.eachCell((cell) => {
            cell.font = {
              bold: true,
            };
          });

          data.forEach((row: any) => {
            worksheet.addRow([
              row.accountId,
              row.accountName,
              row.campaignId,
              row.campaignChannel,
              row.templateId,
              row.templateName,
              row.triggerStatus,
              row.dataSource,
              row.dataFile,
            ]);
          });

          worksheet.columns.forEach((column) => {
            column.width = 20;
          });

          const buffer = await workbook.xlsx.writeBuffer();

          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `campaign_report_${
            data?.id || new Date().toISOString().split("T")[0]
          }.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          enqueueSnackbar(error as string, {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
    });
  };

  const handlePageChange = (pageIndex: number) => {
    setParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  };

  const handleRedirectEditCampaign = (id: number) => {
    navigate(`/campaigns/${id}`);
  };

  const columns = campaignColumns(
    selected,
    setSelected,
    toggleCampaignById,
    handleCopyCampaign,
    handleDownloadReport,
    handleRedirectEditCampaign
  );

  const handleRedirectCreateCampaign = () => {
    navigate("/campaigns/create");
  };

  return (
    <Root>
      <Header>
        <Title>Campaigns</Title>
        <Button
          label="New Campaign"
          icon={<IconAdd fill="white" />}
          iconPosition="left"
          theme="purple"
          onClick={handleRedirectCreateCampaign}
        />
      </Header>
      <Card variant="no-border" className="card-filter">
        <DatePickerField
          name="dateFilter"
          control={control}
          placeholder="September 2024"
          showMonthYearPicker
          format="MMMM yyyy"
        />
        <InputField
          name="search"
          control={control}
          icon={<IconSearch />}
          placeholder="Search By Campaign ID or Campaign Name"
        />
      </Card>
      <Table<DataTypeList>
        columns={columns}
        data={campaignList?.data || []}
        totalRecords={campaignList?.total}
        isLoading={isLoading}
        manualSorting={false}
        pagination={{
          pageIndex: params.page - 1,
          pageSize: 10,
        }}
        enablePagination={true}
        onPageChange={handlePageChange}
        sorting={[]}
        setSorting={() => {}}
      />
    </Root>
  );
};

export default Campaigns;
